import { Component } from '@theme/component';

class DevDrawerNav extends Component {
  /** @type {HTMLDetailsElement | null} */
  #parentDetails = null;

  connectedCallback() {
    super.connectedCallback();
    this.#parentDetails = this.closest('details');
    this.#parentDetails?.addEventListener('toggle', this.#handleDrawerToggle);
    // Same-URL <a> would soft-reload the page and look like the drawer "just closes"
    // (common for page_link items like Get inspired / Shop all when already on that page).
    this.addEventListener('click', this.#handleSameUrlLinkClick);
  }

  disconnectedCallback() {
    super.disconnectedCallback();
    this.#parentDetails?.removeEventListener('toggle', this.#handleDrawerToggle);
    this.removeEventListener('click', this.#handleSameUrlLinkClick);
    this.#parentDetails = null;
  }

  #handleDrawerToggle = () => {
    if (!this.#parentDetails?.open) {
      this.#resetAll();
    }
  };

  /**
   * Prevent same-document navigations inside the drawer so the menu stays open.
   * @param {MouseEvent} event
   */
  #handleSameUrlLinkClick = (event) => {
    if (event.defaultPrevented || event.button !== 0) return;
    if (event.metaKey || event.ctrlKey || event.shiftKey || event.altKey) return;

    const target = event.target;
    if (!(target instanceof Element)) return;

    const link = target.closest('a[href]');
    if (!(link instanceof HTMLAnchorElement) || !this.contains(link)) return;

    let url;
    try {
      url = new URL(link.href, location.href);
    } catch {
      return;
    }

    if (url.origin !== location.origin) return;
    if (url.pathname !== location.pathname) return;
    if (url.search !== location.search) return;
    // Allow in-page hash jumps (e.g. #section)
    if (url.hash && url.hash !== location.hash) return;

    event.preventDefault();
  };

  /**
   * @param {string} handle
   * @param {Event} [event]
   */
  openColumn(handle, event) {
    const target = this.querySelector(`[data-parent="${handle}"]`);
    if (!target || !(target instanceof HTMLElement)) return;

    const level = parseInt(target.dataset.level ?? '0', 10);
    const button =
      event?.target instanceof Element ? event.target.closest('button') : null;

    this.#closeColumnsAtOrAbove(level);
    this.#deactivateButtonsAtLevel(level - 1);

    if (button) {
      button.classList.add('drawer-nav__link--active');
      button.setAttribute('aria-expanded', 'true');
    }

    target.classList.add('is-active');
    this.#promoteColumnImages(target);
    this.dataset.depth = String(level);
    this.#updateDrawerWidth(level);
  }

  /**
   * Native lazy never starts inside display:none L2/L3 (desktop). Flip to eager once the column is shown.
   * @param {HTMLElement} column
   */
  #promoteColumnImages(column) {
    for (const img of column.querySelectorAll('[data-drawer-nav-promo-img]')) {
      if (!(img instanceof HTMLImageElement)) continue;

      if (img.loading === 'lazy') {
        img.loading = 'eager';
      }
    }
  }

  /** @param {Event} [event] */
  closeColumn(event) {
    const column =
      event?.target instanceof Element
        ? event.target.closest('.drawer-nav__column')
        : null;

    if (!column || !(column instanceof HTMLElement)) return;

    const level = parseInt(column.dataset.level ?? '0', 10);

    this.#closeColumnsAtOrAbove(level);
    this.#deactivateButtonsAtLevel(level - 1);

    const newDepth = level - 1;
    this.dataset.depth = String(newDepth);
    this.#updateDrawerWidth(newDepth);
  }

  /** @param {number} level */
  #closeColumnsAtOrAbove(level) {
    for (const col of this.querySelectorAll('.drawer-nav__column.is-active')) {
      if (!(col instanceof HTMLElement)) continue;
      if (parseInt(col.dataset.level ?? '0', 10) >= level) {
        col.classList.remove('is-active');
      }
    }
  }

  /** @param {number} level */
  #deactivateButtonsAtLevel(level) {
    const selector =
      level === 1
        ? '.drawer-nav__column--l1'
        : `.drawer-nav__column--l2.is-active, .drawer-nav__column--l2[data-level="${level}"]`;

    for (const col of this.querySelectorAll(selector)) {
      for (const btn of col.querySelectorAll('.drawer-nav__link--active')) {
        btn.classList.remove('drawer-nav__link--active');
        btn.setAttribute('aria-expanded', 'false');
      }
    }
  }

  /** @param {number} depth */
  #updateDrawerWidth(depth) {
    const drawer = this.closest('.menu-drawer');
    if (!(drawer instanceof HTMLElement)) return;
    drawer.style.setProperty('--drawer-nav-columns', String(depth));
  }

  #resetAll() {
    for (const col of this.querySelectorAll('.drawer-nav__column.is-active')) {
      col.classList.remove('is-active');
    }

    for (const btn of this.querySelectorAll('.drawer-nav__link--active')) {
      btn.classList.remove('drawer-nav__link--active');
      btn.setAttribute('aria-expanded', 'false');
    }

    this.dataset.depth = '1';
    this.#updateDrawerWidth(1);
  }
}

if (!customElements.get('dev-drawer-nav')) {
  customElements.define('dev-drawer-nav', DevDrawerNav);
}
